import { flagKindReach, type FlagKind } from '../schemas/flag-type';

/**
 * What the prod-write confirmation dialog says, for a platform-default write
 * (NOXSCRUM-1529).
 *
 * ADR 0002 §7's confirmation was deferred because no endpoint exposed the
 * resolved account to echo. Under the single shared table (ADR 0006) there is
 * no account to echo any more, and ADR 0007 Q5 says what replaces it: confirm
 * on the **blast radius being written** — "every environment" for the global
 * default, "only `{env}`" for an environment-scoped one. This module covers
 * the global case; the environment-scoped confirmation lands with the
 * environment-matrix write controls (NOXSCRUM-1559).
 *
 * Every variant also names the **kind** of flag (ADR 0009 §5, NOXSCRUM-1670):
 * a `salesforce` default reaches customers' orgs, a `veevart2` one never
 * leaves the platform, and those are different things to agree to.
 *
 * A pure function rather than JSX so it is testable without a DOM — this
 * repo's `apps/web` suite runs under `environment: 'node'`, no jsdom.
 */

export type PlatformDefaultChange =
    | { readonly field: 'enabled'; readonly next: boolean }
    | { readonly field: 'archived'; readonly next: boolean };

const BLAST_RADIUS =
    'This is the platform-wide default — it affects every environment, ' +
    'except one that already has its own environment-specific value.';

export function platformDefaultConfirmCopy(
    flagKey: string,
    change: PlatformDefaultChange,
    kind: FlagKind,
): { readonly title: string; readonly description: string } {
    const reach = flagKindReach(kind);

    if (change.field === 'archived') {
        return change.next
            ? {
                  title: `Archive ${flagKey}?`,
                  description:
                      `This is the real kill switch: ${flagKey} will resolve off ` +
                      `for every museum, in every environment — including any museum ` +
                      `or environment that currently overrides it — until it is ` +
                      `un-archived. ${reach} ${BLAST_RADIUS}`,
              }
            : {
                  title: `Un-archive ${flagKey}?`,
                  description:
                      `${flagKey} resolves off everywhere while archived. Un-archiving ` +
                      `restores its stored value and any overrides — nothing else about ` +
                      `them changes. ${reach} ${BLAST_RADIUS}`,
              };
    }

    return {
        title: `Set the platform default of ${flagKey} to ${change.next ? 'on' : 'off'}?`,
        description:
            `Changes what ${flagKey} resolves to for every museum with no override ` +
            `of their own. ${reach} ${BLAST_RADIUS}`,
    };
}
